import { useMemo, useState } from "react";
import { useAppSelector } from "../../state/store";
import { Grid } from "@mui/material";
import { TournamentLink } from "../../components/TournamentComponents/TournamentLink";
import { Row } from "../../components/Row";
import { getTQuizBackground } from "../../util/tournamentUtils";
import {
  TEvent,
  Tournament,
  TQuiz,
  TRoster,
  TTeamBox,
} from "../../types/tournamentTypes";
import { ReactComponent as LeftArrow } from "../../icons/left-navigation.svg";
import { ReactComponent as Checkmark } from "../../icons/checkmark.svg";
import { ReactComponent as CrossOut } from "../../icons/cross-out.svg";
import { ReactComponent as RightPointer } from "../../icons/right-arrow-pointer.svg";
import { ReactComponent as Stop } from "../../icons/stop-box.svg";
import "./Tournaments.scss";
const iconProps = { width: 24, height: 24, marginRight: 6 };

interface QuizProps {
  getRoundName: (index: number) => string;
  setSelectedQuiz: (newQuiz: TQuiz) => void;
  tournament: Tournament;
  event: TEvent;
  quiz: TQuiz;
  maxRound: number;
  maxSite: number;
  onBack: (nextEvent?: boolean) => void;
  onEdit: () => void;
  hasEditAccess: boolean;
  quizzersMap: Record<number, string>;
  selectedTeam: string | undefined;
}

/**
 * Displays details for a quiz, with buttons to edit and go back and to different quizzes.
 * Has a separate view for small screens.
 */
export function TQuizDisplay(props: QuizProps) {
  const { quiz } = props;
  const { lightMode } = useAppSelector((state) => state.settings);

  // The round/site display
  let roundSite = "";
  if (props.maxRound > 1) roundSite = props.getRoundName(quiz.round);
  if (props.maxRound > 1 && props.maxSite > 1) roundSite += " ";
  if (props.maxSite > 1) roundSite += `S${quiz.site}`;

  // The teams in this quiz
  const teamList = useMemo(() => {
    return [...quiz.teams].sort((t1, t2) => t1.index - t2.index);
  }, [props.quiz]);

  const { isBracket, isBracketWithWindow } = useMemo(() => {
    return {
      isBracket:
        !!props.event.blackLine && props.event.blackLine[quiz.round - 1] > 0,
      isBracketWithWindow: props.event.quizzes.some((q) => q.flag === "W"),
    };
  }, [props.event]);

  // Results from this event for the teams, shown if this is their final quiz
  const preliminaryResults = useMemo(() => {
    const preliminary = props.tournament.preliminaryResults;
    if (!preliminary || isBracketWithWindow) return undefined;
    const preliminaryTitle = preliminary.data.find(
      (d) => d.eventName === props.event.eventName
    )?.title;
    if (!preliminaryTitle) return undefined;
    return teamList.map(
      (team) => preliminary.teams[team.teamName]?.data?.[preliminaryTitle] || 0
    );
  }, [props.quiz, props.event, props.tournament]);

  const adjacent = useMemo(() => {
    const findClosest = (
      direction: "L" | "R" | "U" | "D",
      filter: (q: TQuiz) => boolean
    ): TQuiz | undefined => {
      const isSite = direction === "U" || direction === "D";
      const isLower = direction === "U" || direction === "L";
      let currentQuiz: TQuiz | undefined;
      for (const q of props.event.quizzes) {
        const thisQuizNumber = isSite ? quiz.site : quiz.round;
        const compareNumber = isSite ? q.site : q.round;
        if (
          (isLower
            ? compareNumber < thisQuizNumber
            : thisQuizNumber < compareNumber) &&
          filter(q)
        ) {
          if (currentQuiz) {
            const currentQuizNumber = isSite
              ? currentQuiz.site
              : currentQuiz.round;
            if (
              isLower
                ? currentQuizNumber < compareNumber
                : compareNumber < currentQuizNumber
            )
              currentQuiz = q;
          } else {
            currentQuiz = q;
          }
        }
      }
      return currentQuiz;
    };
    const roundBefore = findClosest("L", (q) => q.site === quiz.site);
    const roundAfter = findClosest("R", (q) => q.site === quiz.site);
    const roundAbove = findClosest("U", (q) => q.round === quiz.round);
    const roundBelow = findClosest("D", (q) => q.round === quiz.round);
    const selectedBefore = findClosest("L", (q) =>
      q.teams.some((t) => t.teamName === props.selectedTeam)
    );
    let selectedAfter: TQuiz | undefined;
    const after = teamList.map((team) => {
      const closest = findClosest("R", (q) =>
        q.teams.some((t) => t.teamName === team.teamName)
      );
      if (team.teamName === props.selectedTeam) selectedAfter = closest;
      return closest;
    });
    return {
      roundBefore,
      roundAfter,
      roundAbove,
      roundBelow,
      selectedBefore,
      after,
      selectedAfter,
    };
  }, [props.event, quiz, props.selectedTeam]);

  // Already eliminated before this quiz
  const isEliminated =
    isBracket &&
    props.event.blackLine &&
    props.event.blackLine[quiz.round - 1] < quiz.site;

  // The team selected for this quiz
  const [selectedTeam, setSelectedTeam] = useState(
    teamList.findIndex((t) => t.teamName === props.selectedTeam)
  );

  const getBracketContent = (team: TTeamBox, index: number) => {
    let bracketResultContent = undefined;
    if (isBracketWithWindow) {
      //resultContent = ` - ${}`
    } else if (preliminaryResults?.[index]) {
      bracketResultContent = ` - ${preliminaryResults[index]} Points`;
    }
    let bracketContent: React.ReactNode | null = null;
    if (isBracket && !quiz.flag?.includes("C")) {
      if (quiz.flag?.includes("W") && team.p === 1) {
        bracketContent = (
          <button className="link" onClick={() => props.onBack(true)}>
            <Checkmark style={{ ...iconProps }} />
            Qualified
          </button>
        );
      } else {
        const next = adjacent.after[index];
        if (next) {
          const nowEliminated =
            !isEliminated &&
            props.event.blackLine &&
            props.event.blackLine[next.round - 1] < next.site;
          const shouldRotate = quiz.site !== next.site;
          bracketContent = (
            <>
              <RightPointer
                style={{
                  ...iconProps,
                  transform: shouldRotate
                    ? next.site < quiz.site
                      ? "rotate(-45deg)"
                      : "rotate(45deg)"
                    : undefined,
                }}
              />
              <button
                onClick={() => props.setSelectedQuiz(next)}
                className="link"
              >
                {`${nowEliminated ? "Eliminated - " : ""}${next.round - 1 > quiz.round ? `${next.round - quiz.round - 1}x Bye - ` : ""}Site ${next.site}${next.flag ? ` (${next.flag})` : ""}`}
              </button>
            </>
          );
        } else {
          if (isEliminated || !isBracketWithWindow) {
            bracketContent = (
              <>
                <Stop style={iconProps} />
                Finished
                {bracketResultContent}
              </>
            );
          } else {
            bracketContent = (
              <>
                <CrossOut style={iconProps} />
                Eliminated and Finished
                {bracketResultContent}
              </>
            );
          }
        }
      }
    }

    if (!bracketContent) return null;
    return (
      <div
        style={{
          marginTop: 20,
          display: "flex",
          alignItems: "center",
        }}
      >
        {bracketContent}
      </div>
    );
  };

  return (
    <div style={{ marginTop: 20 }} className="main-box single-quiz-view">
      <Row>
        <div style={{ marginTop: 17, marginRight: 10 }}>
          <button className="secondary-btn" onClick={() => props.onBack(false)}>
            Back to Quizzes
          </button>
        </div>
        <div
          style={{
            backgroundColor: getTQuizBackground(quiz, lightMode),
            borderRadius: 6,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <button
              className="link"
              aria-label="Site Above"
              style={{ visibility: adjacent.roundAbove ? undefined : "hidden" }}
              onClick={() => props.setSelectedQuiz(adjacent.roundAbove!)}
            >
              <LeftArrow
                width={14}
                height={14}
                style={{ transform: "rotate(90deg)" }}
              />
            </button>
          </div>
          <Row>
            <button
              className="link"
              style={{
                visibility: adjacent.roundBefore ? undefined : "hidden",
              }}
              onClick={() => props.setSelectedQuiz(adjacent.roundBefore!)}
              aria-label="Previous Quiz"
            >
              <LeftArrow width={14} height={14} />
            </button>
            {roundSite && <h3 style={{ margin: 0 }}>{roundSite}</h3>}
            <button
              className="link"
              style={{
                visibility: adjacent.roundAfter ? undefined : "hidden",
                marginLeft: 2,
              }}
              onClick={() => props.setSelectedQuiz(adjacent.roundAfter!)}
              aria-label="Next Quiz"
            >
              <LeftArrow
                width={14}
                height={14}
                style={{ transform: "rotate(180deg)" }}
              />
            </button>
          </Row>
          <div style={{ textAlign: "center" }}>
            <button
              className="link"
              aria-label="Site Below"
              style={{ visibility: adjacent.roundBelow ? undefined : "hidden" }}
              onClick={() => props.setSelectedQuiz(adjacent.roundBelow!)}
            >
              <LeftArrow
                width={14}
                height={14}
                style={{ transform: "rotate(270deg)" }}
              />
            </button>
          </div>
        </div>
      </Row>
      <Row style={{ marginTop: 15, marginBottom: 5 }}>
        {quiz.qm && (
          <>
            <span>QM:</span>
            &nbsp;
            <TournamentLink pageId={quiz.qm} />
          </>
        )}
        {quiz.livestream && (
          <a
            rel="noopener noreferrer"
            target="_blank"
            href={quiz.livestream}
            style={{ marginLeft: 15 }}
          >
            Livestream
          </a>
        )}
        {props.hasEditAccess && (
          <button
            className="link"
            style={{ marginLeft: 10 }}
            onClick={props.onEdit}
          >
            Edit Quiz
          </button>
        )}
      </Row>
      <Grid container spacing={1}>
        {teamList.map((team, index) => {
          const rosterTeam = props.tournament.rosters?.[team.teamName];
          const isSelected = selectedTeam === index;
          const bracketContent = getBracketContent(team, index);

          return (
            <Grid
              key={team.teamName}
              style={{
                width: quiz.isIndividual ? 400 : undefined,
                maxWidth: quiz.isIndividual ? undefined : 250,
              }}
              size={4}
            >
              {!quiz.isIndividual && (
                <div
                  onClick={() => setSelectedTeam(isSelected ? -1 : index)}
                  className={`team-header-box ${isSelected ? "team-selected" : ""}`}
                >
                  <Row style={{ flexWrap: "nowrap" }}>
                    {adjacent.selectedBefore &&
                      team.teamName === props.selectedTeam && (
                        <button
                          className="link"
                          onClick={() =>
                            props.setSelectedQuiz(adjacent.selectedBefore!)
                          }
                          aria-label={`Previous ${team.teamName} Quiz`}
                          key={`previous-${quiz.round}`}
                        >
                          <LeftArrow width={14} height={14} />
                        </button>
                      )}
                    <h4 className="quiz-team">{`${team.p}. ${team.teamName}`}</h4>
                    {adjacent.selectedAfter &&
                      team.teamName === props.selectedTeam && (
                        <button
                          className="link"
                          onClick={() =>
                            props.setSelectedQuiz(adjacent.selectedAfter!)
                          }
                          aria-label={`Next ${team.teamName} Quiz`}
                          key={`next-${quiz.round}`}
                        >
                          <LeftArrow
                            width={14}
                            height={14}
                            style={{ transform: "rotate(180deg)" }}
                          />
                        </button>
                      )}
                  </Row>
                  {(rosterTeam?.ministry || team.seed) && (
                    <div
                      style={{
                        height: 20,
                        position: "relative",
                        fontSize: 14,
                      }}
                    >
                      {rosterTeam?.ministry && (
                        <span>{rosterTeam.ministry}</span>
                      )}
                      {team.seed && (
                        <>
                          <span className="seed-long">
                            {`${team.seed} Seed`}
                          </span>
                          <span className="seed-short">{`(${team.seed})`}</span>
                        </>
                      )}
                    </div>
                  )}
                  {rosterTeam?.coach && (
                    <p
                      style={{ margin: 0, fontSize: 14 }}
                      className="no-wrap-text"
                    >
                      {`Coach: ${rosterTeam.coach}`}
                    </p>
                  )}
                  <p style={{ margin: 0, display: "block" }}>
                    <span style={{ fontWeight: "bold", fontSize: 18 }}>
                      {team.s}
                    </span>
                    <span style={{ marginLeft: 15 }}>{team.e}</span>
                  </p>
                  <button className="see-scores-button">
                    {`${isSelected ? "Hide" : "See"} ${team.teamName} Scores`}
                  </button>
                </div>
              )}
              <div
                className="quizzer-list"
                style={{ display: quiz.isIndividual ? "unset" : undefined }}
              >
                <TeamQuizzerList
                  quiz={quiz}
                  rosterTeam={rosterTeam}
                  team={team}
                  quizzersMap={props.quizzersMap}
                />
                {bracketContent}
              </div>
            </Grid>
          );
        })}
      </Grid>
      {selectedTeam >= 0 && (
        <Grid
          container
          direction="row"
          sx={{
            justifyContent:
              selectedTeam === 0
                ? "left"
                : selectedTeam + 1 === teamList.length
                  ? "right"
                  : "center",
          }}
        >
          <Grid style={{ width: 220 }} className="selected-quizzer-list">
            <p style={{ marginTop: 0, marginBottom: 15, fontSize: 18 }}>
              {teamList[selectedTeam].teamName}
            </p>
            <TeamQuizzerList
              quiz={quiz}
              rosterTeam={
                props.tournament.rosters?.[teamList[selectedTeam].teamName]
              }
              team={teamList[selectedTeam]}
              quizzersMap={props.quizzersMap}
            />
            {getBracketContent(teamList[selectedTeam], selectedTeam)}
          </Grid>
        </Grid>
      )}
    </div>
  );
}

interface TeamQuizzerListProps {
  quiz: TQuiz;
  rosterTeam?: TRoster;
  team: TTeamBox;
  quizzersMap: Record<number, string>;
}

/**
 * The quizzer list for a single team in a quiz
 */
function TeamQuizzerList(props: TeamQuizzerListProps) {
  const quizzerList = props.quiz.isIndividual
    ? props.team.quizzers
    : [...props.team.quizzers].sort((q1, q2) => {
        if (!props.rosterTeam) return 0;
        return (
          props.rosterTeam.quizzers[String(q1.qId)]?.localeCompare(
            props.rosterTeam.quizzers[String(q2.qId)]
          ) || 0
        );
      });

  return (
    <>
      {quizzerList.map((quizzer, qIndex) => {
        const qPoints = quizzer.p * 20;
        const isQuizoutPS = qPoints === 100 && quizzer.e === 0;
        return (
          <Row style={{ marginBottom: 2 }} key={quizzer.qId}>
            {props.quiz.isIndividual && (
              <>
                <div style={{ width: "10%" }}>{`${qIndex + 1}.`}</div>
                <div style={{ width: "60%" }}>
                  {`${
                    props.rosterTeam?.quizzers[quizzer.qId] ||
                    props.quizzersMap[quizzer.qId] ||
                    `Quizzer ${qIndex + 1}`
                  }`}
                </div>
                <div style={{ width: "10%" }}>
                  {isQuizoutPS ? qPoints + 10 : qPoints}
                </div>
                <div style={{ width: "10%" }}>{quizzer.e}</div>
                <div style={{ width: "10%", fontWeight: "bold" }}>
                  {quizzer.score}
                </div>
              </>
            )}
            {!props.quiz.isIndividual && (
              <>
                <div style={{ width: "73%" }} className="no-wrap-text">
                  {`${
                    props.rosterTeam?.quizzers[quizzer.qId] ||
                    props.quizzersMap[quizzer.qId] ||
                    `Quizzer ${qIndex + 1}`
                  }`}
                </div>
                <div style={{ width: "17%" }}>
                  {isQuizoutPS ? qPoints + 10 : qPoints}
                </div>
                <div style={{ width: "10%" }}>{quizzer.e}</div>
              </>
            )}
          </Row>
        );
      })}
    </>
  );
}
